/**
 * Start Assessment Modal for Personal Gap Analysis
 * Allows users to select which assessment types to complete
 */
import React from 'react';
import PropTypes from 'prop-types';
import { X, PlayCircle } from 'lucide-react';
import { ASSESSMENT_TYPES } from '../../utils/personalGapAnalysisConstants';

const StartAssessmentModal = ({
  show,
  onClose,
  selectedTypes,
  onToggleType,
  onStart
}) => {
  if (!show) return null;

  const totalQuestions = 85 +
    (selectedTypes.includes('personal wellbeing') ? 74 : 0) +
    (selectedTypes.includes('personal spiritual') ? 30 : 0);

  const estimatedMinutes = Math.round(totalQuestions * 0.5);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Start Personal Gap Assessment</h2>
              <p className="text-gray-600 mt-2">Select the assessment areas you'd like to evaluate</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>

          {/* Assessment Type Selection */}
          <div className="space-y-4 mb-6">
            {/* Personal Financial - Always Included */}
            <div className="border-2 border-blue-500 bg-blue-50 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex items-center h-5 mr-3">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="font-semibold text-gray-900">
                    Personal Financial <span className="text-blue-600 text-sm">(Required)</span>
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    {ASSESSMENT_TYPES['personal financial'].description}
                    <span className="font-medium text-gray-700">
                      {' '}({ASSESSMENT_TYPES['personal financial'].questionCount} questions,
                      ~{ASSESSMENT_TYPES['personal financial'].estimatedMinutes} minutes)
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Wellbeing - Optional */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                selectedTypes.includes('personal wellbeing')
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-300'
              }`}
              onClick={() => onToggleType('personal wellbeing')}
            >
              <div className="flex items-start">
                <div className="flex items-center h-5 mr-3">
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes('personal wellbeing')}
                    onChange={() => onToggleType('personal wellbeing')}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="font-semibold text-gray-900 cursor-pointer">
                    Personal Wellbeing <span className="text-gray-500 text-sm">(Optional)</span>
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    {ASSESSMENT_TYPES['personal wellbeing'].description}
                    <span className="font-medium text-gray-700">
                      {' '}({ASSESSMENT_TYPES['personal wellbeing'].questionCount} questions,
                      ~{ASSESSMENT_TYPES['personal wellbeing'].estimatedMinutes} minutes)
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Spiritual - Optional */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                selectedTypes.includes('personal spiritual')
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-300'
              }`}
              onClick={() => onToggleType('personal spiritual')}
            >
              <div className="flex items-start">
                <div className="flex items-center h-5 mr-3">
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes('personal spiritual')}
                    onChange={() => onToggleType('personal spiritual')}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="font-semibold text-gray-900 cursor-pointer">
                    Personal Spiritual <span className="text-gray-500 text-sm">(Optional)</span>
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    {ASSESSMENT_TYPES['personal spiritual'].description}
                    <span className="font-medium text-gray-700">
                      {' '}({ASSESSMENT_TYPES['personal spiritual'].questionCount} questions,
                      ~{ASSESSMENT_TYPES['personal spiritual'].estimatedMinutes} minutes)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Total Questions:</span>
              <span className="text-2xl font-bold text-blue-600">{totalQuestions}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Estimated time: ~{estimatedMinutes} minutes (approximately 30 seconds per question)
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={onStart}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition inline-flex items-center justify-center min-h-[44px]"
            >
              <PlayCircle size={20} className="mr-2" />
              Start Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StartAssessmentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleType: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired
};

export default StartAssessmentModal;
